import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Plane, Fuel, Cloud, Building2, GraduationCap, Camera, FileText, Wind, MapPin } from "lucide-react";
import { api } from "../lib/api";

const HERO_IMG = "https://customer-assets.emergentagent.com/job_skyport-info/artifacts/icoeu7ct_cb74101e-823b-4950-8588-fae912c7a136.jpg";
const FACILITY_IMG = "https://images.pexels.com/photos/34145/pexels-photo.jpg";
const RUNWAY_IMG = "https://customer-assets.emergentagent.com/job_skyport-info/artifacts/ixjsutg7_runway.jpg";

function StatLine({ k, v }) {
  return (
    <div className="flex items-baseline justify-between border-b border-white/10 py-3">
      <dt className="text-xs font-mono uppercase tracking-widest text-slate-300">{k}</dt>
      <dd className="text-base sm:text-lg font-display font-medium text-white font-mono-data">{v}</dd>
    </div>
  );
}

export default function Home() {
  const [airnav, setAirnav] = useState(null);
  const [wx, setWx] = useState(null);
  const [info, setInfo] = useState(null);
  const [schools, setSchools] = useState([]);

  useEffect(() => {
    api.get("/airnav-fuel").then((r) => setAirnav(r.data)).catch(() => {});
    api.get("/airport-info").then((r) => setInfo(r.data)).catch(() => {});
    api.get("/flight-schools").then((r) => setSchools(r.data)).catch(() => {});

    let cancelled = false;
    const loadWx = () => api.get("/weather").then((r) => { if (!cancelled) setWx(r.data); }).catch(() => {});
    loadWx();
    const id = setInterval(loadWx, 5 * 60 * 1000); // refresh every 5 minutes
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  return (
    <div data-testid="home-page" className="anim-fadeup">
      {/* HERO */}
      <section className="relative h-[88vh] min-h-[640px] w-full overflow-hidden bg-slate-950">
        <img
          src={HERO_IMG}
          alt="Aerial of Lake Cumberland Regional Airport"
          className="absolute inset-0 h-full w-full object-cover opacity-80"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-slate-950/60 via-slate-950/45 to-slate-950" />
        <div className="absolute inset-0 grid-backdrop opacity-20" />

        <div className="relative h-full max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end pb-16 pt-28">
          <div className="lg:col-span-8">
            <div className="section-label !text-sky-300">KSME · Somerset, Kentucky</div>
            <h1 className="mt-4 font-display font-bold text-white text-4xl sm:text-5xl lg:text-7xl leading-[0.95] tracking-tight">
              Where the<br />bluegrass<br />
              <span className="text-sky-300">meets the sky.</span>
            </h1>
            <p className="mt-6 max-w-xl text-slate-300 leading-relaxed">
              Lake Cumberland Regional Airport (KSME) is South-Central Kentucky's gateway for general aviation —
              corporate, training, and recreational flying just minutes from Somerset and Lake Cumberland.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link
                to="/airport"
                data-testid="hero-cta-airport"
                className="inline-flex items-center gap-2 bg-sky-500 text-slate-950 px-6 py-3 font-semibold hover:bg-sky-400 transition-colors"
              >
                Airport Information <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/fuel"
                data-testid="hero-cta-fuel"
                className="inline-flex items-center gap-2 border border-white/30 text-white px-6 py-3 font-semibold hover:bg-white/10 transition-colors"
              >
                Today's Fuel Prices
              </Link>
            </div>
          </div>

          <div className="lg:col-span-4">
            <div className="bg-slate-900/70 backdrop-blur-md border border-white/10 p-6 lg:p-8">
              <div className="text-xs font-mono uppercase tracking-widest text-sky-300 mb-4">Field Snapshot</div>
              <dl>
                <StatLine k="Identifier" v={info?.identifier || "KSME"} />
                <StatLine k="Elevation" v={`${info?.elevation_ft ?? 927} ft`} />
                <StatLine k="Runway" v="5/23 · 5,801 ft" />
                <StatLine k="CTAF" v={info?.ctaf || "123.075"} />
                <StatLine k="AWOS" v={info?.awos || "118.375"} />
              </dl>
            </div>
          </div>
        </div>
      </section>

      {/* QUICK BENTO: fuel / weather / contact */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 -mt-12 relative z-10 grid grid-cols-1 md:grid-cols-12 gap-6">
        <div data-testid="quick-fuel" className="md:col-span-5 bg-white border border-slate-200 p-8 shadow-md">
          <div className="flex items-center gap-3 mb-4">
            <Fuel className="h-5 w-5 text-sky-600" />
            <div className="section-label !m-0">Fuel · Full Serve · AirNav Live</div>
          </div>
          <div className="grid grid-cols-2 gap-6 mt-4">
            <div>
              <div className="text-xs font-mono uppercase tracking-widest text-slate-500">100LL</div>
              <div className="font-display text-4xl font-bold text-slate-950 mt-1">
                ${airnav?.summary?.avgas_100ll_fs?.toFixed(2) ?? "—"}
              </div>
              <div className="text-xs text-slate-500 mt-1">per gallon · full serve</div>
            </div>
            <div>
              <div className="text-xs font-mono uppercase tracking-widest text-slate-500">Jet A</div>
              <div className="font-display text-4xl font-bold text-slate-950 mt-1">
                ${airnav?.summary?.jet_a_fs?.toFixed(2) ?? "—"}
              </div>
              <div className="text-xs text-slate-500 mt-1">per gallon · full serve</div>
            </div>
          </div>
          <div className="mt-6 pt-4 border-t border-slate-200">
            <div className="text-[11px] font-mono uppercase tracking-widest text-sky-600 mb-2">Volume Discounts</div>
            <div className="grid grid-cols-3 gap-2 text-xs font-mono-data">
              <div><div className="text-slate-500">100–199 gal</div><div className="font-semibold text-slate-950">−$0.10</div></div>
              <div><div className="text-slate-500">200–299 gal</div><div className="font-semibold text-slate-950">−$0.20</div></div>
              <div><div className="text-slate-500">300+ gal</div><div className="font-semibold text-slate-950">−$0.30</div></div>
            </div>
          </div>
          <Link to="/fuel" data-testid="link-fuel-detail" className="mt-6 inline-flex items-center gap-1 text-sm font-medium text-slate-950 hover:gap-2 transition-all">
            All fuel details (incl. self-serve) <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        <div data-testid="quick-weather" className="md:col-span-4 bg-slate-950 text-white p-8">
          <div className="flex items-center gap-3 mb-4">
            <Cloud className="h-5 w-5 text-sky-300" />
            <div className="text-xs font-mono uppercase tracking-widest text-sky-300">Live METAR · KSME</div>
          </div>
          <div className="font-display text-5xl font-bold">{wx?.temperature_f ?? "—"}°F</div>
          <div className="text-sm text-slate-300 mt-1">{wx?.sky || "—"}</div>
          <div className="mt-4 grid grid-cols-2 gap-3 text-sm font-mono-data">
            <div className="flex items-center gap-2"><Wind className="h-4 w-4 text-sky-300" />{wx?.wind_text || "—"}</div>
            <div>Vis {wx?.visibility_sm ?? "—"} sm</div>
            <div>Alt {wx?.altimeter_inhg ? `${wx.altimeter_inhg}"` : "—"}</div>
            <div>Dew {wx?.dewpoint_f ?? "—"}°F</div>
          </div>
          <div className="mt-4 text-[10px] uppercase tracking-widest text-slate-400">
            {wx?.live ? "Live · aviationweather.gov" : (wx?.note || "Connecting to METAR feed…")}
          </div>
        </div>

        <div data-testid="quick-contact" className="md:col-span-3 bg-sky-500 text-slate-950 p-8">
          <div className="text-xs font-mono uppercase tracking-widest mb-4">Operations</div>
          <div className="font-display text-2xl font-bold leading-tight">
            (606) 679-7908
          </div>
          <div className="text-sm mt-2">fbo@flylcra.com</div>
          <div className="text-xs font-mono mt-6 flex items-center gap-1">
            <MapPin className="h-3 w-3" /> 500 W.O. Newell Drive, Somerset KY
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" className="max-w-7xl mx-auto px-6 lg:px-10 pt-28">
        <div className="grid md:grid-cols-12 gap-8">
          <div className="md:col-span-4">
            <div className="section-label">What we offer</div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl lg:text-5xl text-slate-950 mt-3 leading-tight">
              An airport built for serious flying.
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              From corporate jets to first solos, KSME is staffed by aviators who treat your aircraft like our own.
            </p>
          </div>
          <div className="md:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {[
              { icon: Building2, t: "Hangars & Tie-Downs", d: "T-hangars to corporate hangars — month-to-month or long-term. Call for availability.", to: "/hangars" },
              { icon: Fuel, t: "100LL & Jet-A", d: "Self-serve 24/7, full-serve during operating hours, contract fuel welcome.", to: "/fuel" },
              { icon: GraduationCap, t: "Flight Training", d: "Multiple Part 61 schools call KSME home — sport pilot through CFI.", to: "/flight-schools" },
              { icon: Camera, t: "Photos & Facilities", d: "Tour the ramp, pilot lounge, and aircraft based at the field.", to: "/gallery" },
            ].map(({ icon: Icon, t, d, to }) => (
              <Link
                key={t}
                to={to}
                data-testid={`service-${t.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className="group bg-white border border-slate-200 p-6 hover:border-slate-950 transition-colors"
              >
                <Icon className="h-6 w-6 text-sky-600" />
                <div className="font-display text-xl font-semibold mt-4 text-slate-950">{t}</div>
                <p className="text-sm text-slate-600 mt-2 leading-relaxed">{d}</p>
                <div className="mt-4 text-sm font-medium text-slate-950 inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                  Learn more <ArrowRight className="h-4 w-4" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* SPLIT IMAGE / FIELD INFO */}
      <section className="mt-28 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <img src={RUNWAY_IMG} alt="KSME Runway" className="w-full h-[480px] object-cover" />
          </div>
          <div className="md:col-span-5">
            <div className="section-label">Field Information</div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl mt-3 text-slate-950">
              5,801 feet of asphalt. <br />Centered on the bluegrass.
            </h2>
            <p className="mt-4 text-slate-600 leading-relaxed">
              Runway 5/23 is lit, paved asphalt, and approved for day and night operations.
              Published GPS and ILS instrument approaches serve the field — ILS is available on Runway 5 only.
            </p>
            <dl className="mt-8 font-mono-data text-sm border-t border-slate-200">
              {[
                ["Runway", "5/23 — 5,801 × 100 ft, Asphalt"],
                ["Approaches", "RNAV (GPS) RWY 5 / 23 · ILS RWY 5"],
                ["Pattern Altitude", "1,927 ft MSL"],
                ["Lighting", "MIRL · PCL on CTAF"],
                ["Fuel", "100LL · Jet-A · Self-Serve"],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between py-3 border-b border-slate-200">
                  <dt className="text-slate-500">{k}</dt>
                  <dd className="text-slate-950 font-medium">{v}</dd>
                </div>
              ))}
            </dl>
            <Link to="/airport" data-testid="link-airport-info" className="mt-8 inline-flex items-center gap-2 bg-slate-950 text-white px-6 py-3 font-semibold hover:bg-slate-800 transition-colors">
              Full airport information <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* AIR SHOW BANNER */}
      <section data-testid="airshow-banner" className="mt-28 relative overflow-hidden bg-slate-950 text-white">
        <img
          src="https://customer-assets.emergentagent.com/job_skyport-info/artifacts/4op3hx6p_airshow1.jpg"
          alt="Lake Cumberland Air Show"
          className="absolute inset-0 w-full h-full object-cover opacity-35"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-950 via-slate-950/70 to-slate-950/30" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-8">
            <h2 className="font-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight">
              Home of the<br />
              <span className="text-sky-300">Lake Cumberland Air Show.</span>
            </h2>
            <p className="mt-5 text-slate-300 max-w-xl leading-relaxed">
              From warbirds and aerobatic teams to formation passes over Somerset, KSME proudly hosts the
              Lake Cumberland Air Show — a yearly showcase of aviation for the whole community.
            </p>
          </div>
          <div className="md:col-span-4 flex flex-col gap-3">
            <a
              href="https://lakecumberlandairshow.com"
              target="_blank"
              rel="noreferrer"
              data-testid="airshow-website"
              className="inline-flex items-center gap-2 bg-sky-500 text-slate-950 px-6 py-4 font-semibold hover:bg-sky-400 transition-colors"
            >
              Visit Air Show Website <ArrowRight className="h-4 w-4" />
            </a>
            <Link
              to="/gallery"
              data-testid="airshow-cta"
              className="inline-flex items-center gap-2 border border-white/25 text-white px-6 py-4 font-semibold hover:bg-white/10 transition-colors"
            >
              View Air Show Photos <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* FLIGHT SCHOOLS preview */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-28">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="section-label">Train at KSME</div>
            <h2 className="font-display font-bold text-3xl sm:text-4xl mt-3">Flight schools based here.</h2>
          </div>
          <Link to="/flight-schools" data-testid="link-all-schools" className="text-sm font-medium underline underline-offset-4 decoration-sky-500 decoration-2">View all</Link>
        </div>
        <div className="mt-10 grid md:grid-cols-2 gap-6">
          {schools.slice(0, 2).map((s) => (
            <div key={s.id} data-testid={`home-school-${s.id}`} className="bg-white border border-slate-200 p-6">
              <div className="font-display text-2xl font-semibold text-slate-950">{s.name}</div>
              <p className="text-sm text-slate-600 mt-2">{s.description}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                {(s.programs || []).map((p) => (
                  <span key={p} className="text-[11px] font-mono uppercase tracking-widest border border-slate-300 px-2 py-1">{p}</span>
                ))}
              </div>
              <div className="mt-5 text-xs font-mono text-slate-500">{s.phone} · {s.email}</div>
            </div>
          ))}
          {schools.length === 0 && (
            <div className="md:col-span-2 text-slate-500 text-sm">No flight schools listed yet.</div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="mt-28 bg-slate-950 text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7">
            <div className="section-label !text-sky-300">Flying in?</div>
            <h2 className="font-display font-bold text-3xl sm:text-5xl mt-3">
              Cleared to land. <br />We'll have the ramp ready.
            </h2>
            <p className="mt-5 text-slate-300 max-w-xl leading-relaxed">
              Call ops on the way in, top off the tanks, grab a courtesy car, and head into downtown Somerset.
              We're here to make general aviation easy.
            </p>
          </div>
          <div className="md:col-span-5">
            <Link to="/contact" data-testid="cta-contact" className="bg-sky-500 text-slate-950 px-6 py-5 font-semibold flex items-center justify-between">
              Contact Ops <ArrowRight />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
